import React, { useState, useEffect } from "react";
import { Auth, API } from "aws-amplify";
import { Switch, Route, Redirect } from "react-router-dom";
import { withRouter } from "react-router";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  AppBar,
  Toolbar,
  Hidden,
  Drawer,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from "@material-ui/core";
import { Menu, ImageSearch, CloudUpload, ExitToApp } from "@material-ui/icons";

import SalesSearch from "./search";
import UploadData from "./upload";

export const AppContext = React.createContext();

const drawerWidth = 240;
const useStyles = makeStyles((theme) => {
  return {
    appBar: {
      zIndex: theme.zIndex.drawer + 1,
      background: theme.palette.primary.dark,
    },
    menuButton: {
      marginRight: theme.spacing(2),
      [theme.breakpoints.up("sm")]: {
        display: "none",
      },
    },
    drawer: {
      [theme.breakpoints.up("sm")]: {
        width: drawerWidth,
        flexShrink: 0,
      },
    },
    drawerPaper: {
      width: drawerWidth,
    },
    content: {
      [theme.breakpoints.up("sm")]: {
        marginLeft: drawerWidth,
      },
      flexGrow: 1,
      padding: theme.spacing(3),
    },
    toolbar: theme.mixins.toolbar,
  };
});

const App = (props) => {
  const { authState, history } = props;
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [locations, setLocations] = useState([]);

  const classes = useStyles();
  const theme = useTheme();

  const getLocations = async () => {
    const locations = await API.get("backend", "/locations");
    setLocations(locations);
  };

  useEffect(() => {
    const request = async () => {
      if (authState === "signedIn") {
        const user = await Auth.currentSession();
        setIsAdmin(
          (user["idToken"]["payload"]["cognito:groups"] || []).includes("admin")
        );
        getLocations();
      } else {
        setIsAdmin(false);
      }
    };
    request();
  }, [authState]);

  if (authState !== "signedIn") return null;

  const drawer = (
    <>
      <List>
        <ListItem
          button
          onClick={() => {
            history.push("/search");
            setDrawerVisible(false);
          }}
        >
          <ListItemIcon>
            <ImageSearch />
          </ListItemIcon>
          <ListItemText>Sales search</ListItemText>
        </ListItem>
        {isAdmin && (
          <ListItem
            button
            onClick={() => {
              history.push("/upload");
              setDrawerVisible(false);
            }}
          >
            <ListItemIcon>
              <CloudUpload />
            </ListItemIcon>
            <ListItemText>Upload data</ListItemText>
          </ListItem>
        )}
      </List>

      <Divider />

      <List>
        <ListItem
          button
          onClick={() =>
            Auth.signOut().then(() => {
              history.push("/");
              setDrawerVisible(false);
            })
          }
        >
          <ListItemIcon>
            <ExitToApp />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </ListItem>
      </List>
    </>
  );

  return (
    <AppContext.Provider value={{ locations }}>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setDrawerVisible(!drawerVisible)}
            className={classes.menuButton}
          >
            <Menu />
          </IconButton>
          <Typography variant="h5" noWrap>
            Cyprus Comparable Sales
          </Typography>
        </Toolbar>
      </AppBar>

      <nav className={classes.drawer}>
        <Hidden smUp implementation="css">
          <Drawer
            variant="temporary"
            anchor={theme.direction === "rtl" ? "right" : "left"}
            open={drawerVisible}
            onClose={() => setDrawerVisible(!drawerVisible)}
            classes={{
              paper: classes.drawerPaper,
            }}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile
            }}
          >
            {drawer}
          </Drawer>
        </Hidden>

        <Hidden xsDown implementation="css">
          <Drawer
            classes={{
              paper: classes.drawerPaper,
            }}
            variant="permanent"
            open
          >
            <div className={classes.toolbar} />
            {drawer}
          </Drawer>
        </Hidden>
      </nav>

      <main className={classes.content}>
        <div className={classes.toolbar} />

        <Switch>
          <Route path="/search">
            <SalesSearch locations={locations} />
          </Route>

          {isAdmin && (
            <Route path="/upload">
              <UploadData />
            </Route>
          )}

          <Route path="*">
            <Redirect to="/search" />
          </Route>
        </Switch>
      </main>
    </AppContext.Provider>
  );
};

export default withRouter(App);
